/**
 * Calendar Filters Component
 *
 * Filter controls for calendar events by type, project type, and assignee.
 */

import { CalendarDays, CheckSquare, Truck, Wrench, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { CalendarFilters as CalendarFiltersType, CalendarEventType, ProjectType } from '../types';
import { EVENT_COLORS } from '../utils';

interface CalendarFiltersProps {
  filters: CalendarFiltersType;
  onFiltersChange: (filters: CalendarFiltersType) => void;
}

// Note: Milestones were removed from calendar view - they now appear in the project activity log
const EVENT_TYPE_OPTIONS: { value: CalendarEventType; label: string; icon: React.ElementType }[] = [
  { value: 'project', label: 'Projects', icon: CalendarDays },
  { value: 'task', label: 'Tasks', icon: CheckSquare },
];

const PROJECT_TYPE_OPTIONS: { value: ProjectType; label: string; icon: React.ElementType; colorKey: keyof typeof EVENT_COLORS }[] = [
  { value: 'installation', label: 'Installation', icon: Wrench, colorKey: 'installation' },
  { value: 'shipping', label: 'Shipping', icon: Truck, colorKey: 'shipping' },
  { value: 'calibration', label: 'Calibration', icon: Package, colorKey: 'calibration' },
];

export function CalendarFilters({ filters, onFiltersChange }: CalendarFiltersProps) {
  const toggleEventType = (type: CalendarEventType) => {
    const newTypes = filters.types.includes(type)
      ? filters.types.filter((t) => t !== type)
      : [...filters.types, type];

    // Ensure at least one type is selected
    if (newTypes.length === 0) return;

    onFiltersChange({ ...filters, types: newTypes });
  };

  const toggleProjectType = (type: ProjectType) => {
    const newTypes = filters.projectTypes.includes(type)
      ? filters.projectTypes.filter((t) => t !== type)
      : [...filters.projectTypes, type];

    // Ensure at least one type is selected
    if (newTypes.length === 0) return;

    onFiltersChange({ ...filters, projectTypes: newTypes });
  };

  const toggleIncludeCompleted = () => {
    onFiltersChange({ ...filters, includeCompleted: !filters.includeCompleted });
  };

  return (
    <div className="space-y-4">
      {/* Event Types */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground">Event Types</Label>
        <div className="flex flex-wrap gap-2">
          {EVENT_TYPE_OPTIONS.map((option) => {
            const isActive = filters.types.includes(option.value);
            const Icon = option.icon;
            return (
              <Button
                key={option.value}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                className="h-8"
                onClick={() => toggleEventType(option.value)}
              >
                <Icon className="h-3 w-3 mr-1" />
                {option.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Project Types */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground">Project Types</Label>
        <div className="flex flex-wrap gap-2">
          {PROJECT_TYPE_OPTIONS.map((option) => {
            const isActive = filters.projectTypes.includes(option.value);
            const Icon = option.icon;
            const color = EVENT_COLORS[option.colorKey];
            return (
              <Button
                key={option.value}
                variant="outline"
                size="sm"
                className="h-8"
                style={{
                  backgroundColor: isActive ? `${color.bg}15` : undefined,
                  borderColor: isActive ? color.border : undefined,
                  color: isActive ? color.bg : undefined,
                }}
                onClick={() => toggleProjectType(option.value)}
              >
                <Icon className="h-3 w-3 mr-1" />
                {option.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Include Completed */}
      <div className="flex items-center gap-2">
        <Switch
          id="include-completed"
          checked={filters.includeCompleted}
          onCheckedChange={toggleIncludeCompleted}
        />
        <Label htmlFor="include-completed" className="text-sm cursor-pointer">
          Show completed items
        </Label>
      </div>
    </div>
  );
}
